const STORAGE_KEY = "gymrite_tracker_questions";

export const QUESTION_MODES = {
  GENERAL: "general",
  MEASURABLE: "measurable",
  COACH_REVIEW: "coach_review",
};

export const MEASURE_DIRECTIONS = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
};

export const MEASURE_TYPES = [
  {
    value: "weight",
    label: "Weight",
    defaultDirection: MEASURE_DIRECTIONS.ASCENDING,
    units: ["kg", "lbs", "st"],
  },
  {
    value: "time",
    label: "Time",
    defaultDirection: MEASURE_DIRECTIONS.ASCENDING,
    units: ["sec", "min", "hh:mm:ss"],
  },
  {
    value: "distance",
    label: "Distance",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["m", "km", "miles", "yards"],
  },
  {
    value: "height",
    label: "Height",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["cm", "m", "ft", "in"],
  },
  {
    value: "percentage",
    label: "Percentage",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["%"],
  },
  {
    value: "reps",
    label: "Reps / Count",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["reps", "count"],
  },
  {
    value: "score",
    label: "Score / Points",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["pts", "score"],
  },
  {
    value: "custom",
    label: "Custom",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: [],
  },
];

export const QUESTION_FIELD_TYPES = [
  { value: "short_text", label: "Short Text" },
  { value: "long_text", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "scale", label: "Scale (1–10)" },
  { value: "email", label: "Email Address" },
  { value: "phone", label: "Phone Number" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "checkbox", label: "Checkbox" },
  { value: "yes_no", label: "Yes / No" },
  { value: "status", label: "Status" },
  { value: "coach", label: "Coach" },
  { value: "gym", label: "Gym" },
  { value: "programme", label: "Programme" },
  { value: "review_date", label: "Review Date" },
  { value: "follow_up_date", label: "Follow-up Date" },
  { value: "member", label: "Member" },
  { value: "image_upload", label: "Image Upload" },
  { value: "file_upload", label: "File Upload" },
];

export const getFieldTypeLabel = (value) =>
  QUESTION_FIELD_TYPES.find((item) => item.value === value)?.label || value || "—";

export const getQuestionModeLabel = (mode) => {
  if (mode === QUESTION_MODES.MEASURABLE) return "Measurable";
  if (mode === QUESTION_MODES.COACH_REVIEW) return "Coach review";
  return "General";
};

export const getMeasureTypeLabel = (value) =>
  MEASURE_TYPES.find((item) => item.value === value)?.label || value || "—";

export const getMeasureTypeMeta = (value) =>
  MEASURE_TYPES.find((item) => item.value === value) || null;

export const isScaleFieldType = (fieldType) => fieldType === "scale";

/** Build scale options e.g. 1..10 */
export const getScaleOptions = (min = 1, max = 10) => {
  const start = Math.max(0, Number(min) || 1);
  const end = Math.max(start, Number(max) || 10);
  const list = [];
  for (let n = start; n <= end; n += 1) list.push(String(n));
  return list;
};

const pickText = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

/**
 * When a tracker uses a library question, fill empty placeholder / help / type
 * from the library record so hints show on the form.
 */
export const enrichQuestionFromLibrary = (question, libraryItem) => {
  if (!question) return question;
  if (!libraryItem) return { ...question };

  const isScale =
    isScaleFieldType(question.fieldType) ||
    isScaleFieldType(libraryItem.fieldType);
  const scaleMin = question.scaleMin ?? libraryItem.scaleMin ?? 1;
  const scaleMax = question.scaleMax ?? libraryItem.scaleMax ?? 10;

  return {
    ...question,
    label: pickText(question.label, libraryItem.label),
    placeholder: pickText(question.placeholder, libraryItem.placeholder),
    helpText: pickText(question.helpText, libraryItem.helpText),
    fieldType: question.fieldType || libraryItem.fieldType || "short_text",
    mode: question.mode || libraryItem.mode || QUESTION_MODES.GENERAL,
    measureType: question.measureType || libraryItem.measureType || "",
    units:
      Array.isArray(question.units) && question.units.length
        ? question.units
        : libraryItem.units || [],
    defaultUnit: question.defaultUnit || libraryItem.defaultUnit || "",
    measureDirection:
      question.measureDirection ||
      libraryItem.measureDirection ||
      MEASURE_DIRECTIONS.DESCENDING,
    scaleMin,
    scaleMax,
    options:
      Array.isArray(question.options) && question.options.length
        ? question.options
        : isScale
          ? getScaleOptions(scaleMin, scaleMax)
          : Array.isArray(libraryItem.options)
            ? libraryItem.options
            : [],
    measurable:
      Boolean(question.measurable) ||
      question.mode === QUESTION_MODES.MEASURABLE ||
      libraryItem.mode === QUESTION_MODES.MEASURABLE,
  };
};

export const needsOptions = (fieldType) =>
  ["dropdown", "multiple_choice", "checkbox", "status"].includes(fieldType);

export const createEmptyQuestion = () => ({
  id: `question_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  label: "",
  placeholder: "",
  helpText: "",
  fieldType: "short_text",
  mode: QUESTION_MODES.GENERAL,
  measureType: "",
  units: [],
  defaultUnit: "",
  customUnits: [],
  measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  scaleMin: 1,
  scaleMax: 10,
  options: [],
  status: "active",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const now = () => new Date().toISOString();

const buildSeedQuestion = (data) => ({
  measureType: "",
  units: [],
  defaultUnit: "",
  customUnits: [],
  measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  scaleMin: 1,
  scaleMax: 10,
  options: [],
  placeholder: "",
  helpText: "",
  status: "active",
  createdAt: now(),
  updatedAt: now(),
  ...data,
});

/** Built-in questions for GymRite Trackers / Journeys */
export const DEFAULT_TRACKER_QUESTIONS = [
  // Person / lead capture
  buildSeedQuestion({
    id: "q_first_name",
    label: "First Name",
    placeholder: "Enter first name",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_last_name",
    label: "Last Name",
    placeholder: "Enter last name",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_email",
    label: "Email Address",
    placeholder: "name@example.com",
    fieldType: "email",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_phone",
    label: "Phone Number",
    placeholder: "Enter phone number",
    fieldType: "phone",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_emergency_contact",
    label: "Emergency Contact",
    placeholder: "Name and phone number",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_preferred_gym",
    label: "Preferred Gym",
    placeholder: "Select preferred gym",
    fieldType: "gym",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_programme",
    label: "Programme",
    placeholder: "Select programme",
    fieldType: "programme",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_assigned_coach",
    label: "Assigned Coach",
    placeholder: "Select assigned coach",
    fieldType: "coach",
    mode: QUESTION_MODES.GENERAL,
  }),

  // Goals / journeys
  buildSeedQuestion({
    id: "q_primary_goal",
    label: "Primary Goal",
    placeholder: "What do you want to achieve?",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_fitness_goal",
    label: "Fitness Goal",
    placeholder: "Describe your fitness goal",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_quarterly_goal",
    label: "Quarterly Goal",
    placeholder: "Goal for this quarter",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_attendance_commitment",
    label: "Attendance Commitment",
    placeholder: "Select sessions per week",
    fieldType: "dropdown",
    mode: QUESTION_MODES.GENERAL,
    options: [
      "1 session / week",
      "2 sessions / week",
      "3 sessions / week",
      "4 sessions / week",
      "5+ sessions / week",
    ],
  }),
  buildSeedQuestion({
    id: "q_current_fitness",
    label: "Current Fitness Level",
    placeholder: "Select your level",
    fieldType: "multiple_choice",
    mode: QUESTION_MODES.GENERAL,
    options: ["Beginner", "Intermediate", "Advanced"],
  }),
  buildSeedQuestion({
    id: "q_experience",
    label: "Training Experience",
    placeholder: "Tell us about your training background",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_medical_notes",
    label: "Medical Notes / Questionnaire",
    placeholder: "Any injuries, conditions, or notes",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_coach_notes",
    label: "Coach Notes",
    placeholder: "Internal coach notes",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_member_notes",
    label: "Member Notes",
    placeholder: "Anything else we should know?",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),

  // Status / yes-no journey checks
  buildSeedQuestion({
    id: "q_status",
    label: "Status",
    fieldType: "status",
    mode: QUESTION_MODES.GENERAL,
    options: [
      "New Lead",
      "Contacted",
      "Consultation Booked",
      "Registered",
      "Active",
      "In Progress",
      "Attended",
      "Completed",
      "Journey Completed",
      "Paused",
      "Cancelled",
      "Withdrawn",
    ],
  }),
  buildSeedQuestion({
    id: "q_attendance_honoured",
    label: "Attendance Honoured",
    fieldType: "yes_no",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_goal_achieved",
    label: "Goal Achieved",
    fieldType: "yes_no",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_milestone",
    label: "Milestone",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_review_date",
    label: "Review Date",
    fieldType: "review_date",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_follow_up_date",
    label: "Follow-up Date",
    fieldType: "follow_up_date",
    mode: QUESTION_MODES.GENERAL,
  }),

  // Media
  buildSeedQuestion({
    id: "q_progress_photo",
    label: "Progress Photo",
    fieldType: "image_upload",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_challenge_photo",
    label: "Challenge Photo",
    fieldType: "image_upload",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_challenge_video",
    label: "Challenge Video",
    fieldType: "file_upload",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_supporting_file",
    label: "Supporting File",
    fieldType: "file_upload",
    mode: QUESTION_MODES.GENERAL,
  }),

  // Measurable – body / journey
  buildSeedQuestion({
    id: "q_starting_weight",
    label: "Starting Weight",
    placeholder: "Enter starting weight",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "weight",
    units: ["kg", "lbs"],
    defaultUnit: "kg",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_current_weight",
    label: "Current Weight",
    placeholder: "Enter current weight",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "weight",
    units: ["kg", "lbs"],
    defaultUnit: "kg",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_target_weight",
    label: "Target Weight",
    placeholder: "Enter target weight",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "weight",
    units: ["kg", "lbs"],
    defaultUnit: "kg",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_body_fat",
    label: "Body Fat Percentage",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "percentage",
    units: ["%"],
    defaultUnit: "%",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_height",
    label: "Height",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "height",
    units: ["cm", "ft", "in"],
    defaultUnit: "cm",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),

  // Measurable – performance / challenges / GymTV
  buildSeedQuestion({
    id: "q_pushup_count",
    label: "Push-up Count",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "reps",
    units: ["reps", "count"],
    defaultUnit: "reps",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_completed_actions",
    label: "Actions Completed",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "reps",
    units: ["count"],
    defaultUnit: "count",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_sessions_attended",
    label: "Sessions Attended",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "reps",
    units: ["count"],
    defaultUnit: "count",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_hyrox_time",
    label: "HYROX Completion Time",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "time",
    units: ["min", "hh:mm:ss", "sec"],
    defaultUnit: "hh:mm:ss",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_race_time",
    label: "Race / Benchmark Time",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "time",
    units: ["min", "hh:mm:ss", "sec"],
    defaultUnit: "min",
    measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  }),
  buildSeedQuestion({
    id: "q_run_distance",
    label: "Run Distance",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "distance",
    units: ["km", "miles", "m"],
    defaultUnit: "km",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_total_points",
    label: "Total Points",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "score",
    units: ["pts", "score"],
    defaultUnit: "pts",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_challenge_score",
    label: "Challenge Score",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "score",
    units: ["pts", "score"],
    defaultUnit: "score",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_lift_weight",
    label: "Lift Weight",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "weight",
    units: ["kg", "lbs"],
    defaultUnit: "kg",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_completion_percentage",
    label: "Completion Percentage",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "percentage",
    units: ["%"],
    defaultUnit: "%",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),
  buildSeedQuestion({
    id: "q_attendance_percentage",
    label: "Attendance Percentage",
    fieldType: "number",
    mode: QUESTION_MODES.MEASURABLE,
    measureType: "percentage",
    units: ["%"],
    defaultUnit: "%",
    measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  }),

  // Journey check-ins / reviews (GymRite examples)
  buildSeedQuestion({
    id: "q_how_feeling",
    label: "How are you feeling?",
    fieldType: "dropdown",
    mode: QUESTION_MODES.GENERAL,
    options: ["Great", "Good", "OK", "Struggling", "Need support"],
  }),
  buildSeedQuestion({
    id: "q_goal_progress",
    label: "Goal Progress",
    fieldType: "dropdown",
    mode: QUESTION_MODES.GENERAL,
    options: [
      "On track",
      "Fully Achieved",
      "Partially Achieved",
      "Not Achieved",
      "Needs reset",
    ],
  }),
  buildSeedQuestion({
    id: "q_energy_level",
    label: "Energy Level",
    placeholder: "Rate from 1 (low) to 10 (high)",
    fieldType: "scale",
    mode: QUESTION_MODES.GENERAL,
    scaleMin: 1,
    scaleMax: 10,
    options: getScaleOptions(1, 10),
  }),
  buildSeedQuestion({
    id: "q_motivation",
    label: "Motivation",
    placeholder: "How motivated do you feel today? (1–10)",
    fieldType: "scale",
    mode: QUESTION_MODES.GENERAL,
    scaleMin: 1,
    scaleMax: 10,
    options: getScaleOptions(1, 10),
  }),
  buildSeedQuestion({
    id: "q_sleep_quality",
    label: "Sleep Quality",
    placeholder: "How was your sleep?",
    fieldType: "dropdown",
    mode: QUESTION_MODES.GENERAL,
    options: ["Excellent", "Good", "Fair", "Poor"],
  }),
  buildSeedQuestion({
    id: "q_nutrition_adherence",
    label: "Nutrition Adherence",
    fieldType: "dropdown",
    mode: QUESTION_MODES.GENERAL,
    options: ["Excellent", "Good", "Inconsistent", "Off plan"],
  }),
  buildSeedQuestion({
    id: "q_blockers",
    label: "Blockers / Challenges",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_next_focus",
    label: "Next Focus",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_wins_this_period",
    label: "Wins this period",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_consultation_reason",
    label: "Reason for consultation",
    fieldType: "long_text",
    mode: QUESTION_MODES.GENERAL,
  }),
  buildSeedQuestion({
    id: "q_preferred_times",
    label: "Preferred times",
    fieldType: "checkbox",
    mode: QUESTION_MODES.GENERAL,
    options: ["Morning", "Lunch", "Evening", "Weekend"],
  }),
  buildSeedQuestion({
    id: "q_week_stage",
    label: "Current Week / Stage",
    fieldType: "short_text",
    mode: QUESTION_MODES.GENERAL,
  }),

  // Coach review library — answered by coaches on a review, not by members
  buildSeedQuestion({
    id: "q_cr_coach_notes",
    label: "Coach notes",
    fieldType: "long_text",
    mode: QUESTION_MODES.COACH_REVIEW,
    helpText: "Notes from completing this review (visible to coaches/admins).",
  }),
  buildSeedQuestion({
    id: "q_cr_progress_rating",
    label: "Overall progress",
    placeholder: "Rate overall progress from 1 to 10",
    fieldType: "scale",
    mode: QUESTION_MODES.COACH_REVIEW,
    scaleMin: 1,
    scaleMax: 10,
    options: getScaleOptions(1, 10),
  }),
  buildSeedQuestion({
    id: "q_cr_adherence",
    label: "Plan adherence",
    fieldType: "dropdown",
    mode: QUESTION_MODES.COACH_REVIEW,
    options: ["On plan", "Mostly on plan", "Off plan"],
  }),
  buildSeedQuestion({
    id: "q_cr_member_ready",
    label: "Ready for next block?",
    fieldType: "yes_no",
    mode: QUESTION_MODES.COACH_REVIEW,
  }),
  buildSeedQuestion({
    id: "q_cr_next_actions",
    label: "Actions for the member",
    fieldType: "long_text",
    mode: QUESTION_MODES.COACH_REVIEW,
    helpText: "What should they focus on until the next review?",
  }),
  buildSeedQuestion({
    id: "q_cr_follow_up_needed",
    label: "Follow-up needed?",
    fieldType: "yes_no",
    mode: QUESTION_MODES.COACH_REVIEW,
  }),
];

const seedQuestionsIfEmpty = () => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(DEFAULT_TRACKER_QUESTIONS)
  );
  return [...DEFAULT_TRACKER_QUESTIONS];
};

/** Keep library in sync with built-in seeds without wiping custom questions. */
export const mergeMissingDefaultQuestions = (existing = []) => {
  const byId = new Map((existing || []).map((q) => [q.id, q]));
  let changed = false;
  DEFAULT_TRACKER_QUESTIONS.forEach((seed) => {
    if (!byId.has(seed.id)) {
      byId.set(seed.id, seed);
      changed = true;
    }
  });
  const merged = Array.from(byId.values());
  if (changed) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }
  return merged;
};

export const getQuestions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedQuestionsIfEmpty();

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return seedQuestionsIfEmpty();
    }
    return mergeMissingDefaultQuestions(parsed);
  } catch {
    return seedQuestionsIfEmpty();
  }
};

/** Force reload built-in starter questions (overwrites local list) */
export const resetQuestionsToDefaults = () => seedQuestionsIfEmpty();

export const getQuestionById = (id) =>
  getQuestions().find((question) => question.id === id) || null;

export const saveQuestion = (question) => {
  const questions = getQuestions();
  const index = questions.findIndex((item) => item.id === question.id);
  const payload = {
    ...question,
    updatedAt: new Date().toISOString(),
  };

  if (index >= 0) {
    questions[index] = payload;
  } else {
    questions.unshift(payload);
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
  return payload;
};

export const normalizeQuestion = (q) => {
  if (!q) return q;
  const id = q._id || q.id;
  return {
    ...q,
    id,
    _id: id,
  };
};

export const cacheQuestionsFromApi = (apiQuestions = []) => {
  if (!Array.isArray(apiQuestions)) return [];
  const normalized = apiQuestions.map(normalizeQuestion);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const deleteQuestion = (id) => {
  const questions = getQuestions().filter(
    (question) => (question._id || question.id) !== id
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
  return questions;
};
